/**
 * The distribution themes catalog as `ThemeLook`s, the way a drawing reads a theme: its colours,
 * chart colours, font files and preview kit (pd7 CONTRACT §2.1, §3.5). An uploaded PowerPoint
 * template's design maps to a catalog theme first (`masterLookAsTheme`), so it draws the same way.
 * Pure; no React.
 */
import { darkThemeColors, lightThemeColors } from "@magicbi/ui-library";
import { rawHex } from "../../design-system/tokens";
import { toChartTheme, type ChartThemeSource } from "../../utils/chartTheme";
import { contrastRatio } from "../../utils/distributionCopyView";
import type { SlideFrame } from "../../components/molecules/SlideCanvas/SlideCanvas.types";
import type { ThemeFont, ThemeKit, ThemeKitBox, ThemeKitSlide, ThemeLook } from "../../utils/themeLook.types";
import type {
  DistributionMasterLookApi,
  DistributionThemeApi,
  DistributionThemeFontApi,
  DistributionThemeKitApi,
  DistributionThemeKitBoxApi,
  DistributionThemeKitSlideApi,
} from "./distributions.types";

type Palette = DistributionThemeApi["palette"];
type Tone = ThemeLook["tone"];

/** The kit layout this build reads (CONTRACT §1.1); a kit of another version draws from colours alone. */
export const THEME_KIT_VERSION = 1;

/**
 * Text this far from its background reads (WCAG's minimum for large text). A palette key that
 * doesn't reach it is skipped: the built-in themes keep legacy dark-canvas keys (a white
 * `textHigh`) next to the light canvas their slides are drawn on.
 */
export const MIN_READABLE_CONTRAST = 3;

/** The palette keys each colour is read from, in order (CONTRACT §3.5). */
export const PALETTE_KEYS = {
  background: ["canvasLight", "canvasDark", "bookendBg"],
  text: ["textHigh", "textDark", "textWhite"],
  muted: ["textMedium", "textMuted"],
  accent: ["purplePrimary", "accentPurple", "accentRed"],
  positive: ["accentPositive"],
  negative: ["accentNegative"],
  rule: ["ruleGray"],
  card: ["canvasLight"],
} as const;

const TOKENS = { light: lightThemeColors, dark: darkThemeColors } as const;

const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

/** A palette value as `#RRGGBB`, or null when it isn't a hex colour. */
export const toHexColour = (value: unknown): string | null => {
  if (typeof value !== "string" || !HEX.test(value.trim())) return null;
  const hex = value.trim().slice(1);
  const full = hex.length === 3 ? hex.split("").map((c) => c + c).join("") : hex;
  return `#${full.toUpperCase()}`;
};

const coloursAt = (palette: Palette, keys: readonly string[]): string[] =>
  keys.map((key) => toHexColour(palette?.[key])).filter((colour): colour is string => colour !== null);

/** The palette's first colour among `keys`, else null. */
export const paletteColour = (palette: Palette, keys: readonly string[]): string | null => coloursAt(palette, keys)[0] ?? null;

/** The theme's own accent (its swatch in pickers), else null. */
export const paletteAccent = (palette: Palette): string | null => paletteColour(palette, PALETTE_KEYS.accent);

/** The first of `keys` that reads on `background`, else whichever app text colour reads best on it. */
const readableOn = (background: string, palette: Palette, keys: readonly string[], appColours: readonly string[]): string =>
  coloursAt(palette, keys).find((colour) => contrastRatio(colour, background) >= MIN_READABLE_CONTRAST) ??
  [...appColours].sort((a, b) => contrastRatio(b, background) - contrastRatio(a, background))[0];

/** The theme's colours from its palette; whatever it lacks comes from the app's tokens for its tone. */
export const themeLookColours = (palette: Palette, tone: Tone): ThemeLook["colors"] => {
  const background = paletteColour(palette, PALETTE_KEYS.background) ?? TOKENS[tone].background.primary;
  const ownTone = (key: "primary" | "secondary") => [TOKENS[tone].text[key], TOKENS[tone === "light" ? "dark" : "light"].text[key]];
  return {
    background,
    text: readableOn(background, palette, PALETTE_KEYS.text, ownTone("primary")),
    muted: readableOn(background, palette, PALETTE_KEYS.muted, ownTone("secondary")),
    accent: paletteColour(palette, PALETTE_KEYS.accent) ?? TOKENS[tone].primary[500],
    positive: paletteColour(palette, PALETTE_KEYS.positive) ?? rawHex.accent.green500,
    negative: paletteColour(palette, PALETTE_KEYS.negative) ?? rawHex.accent.red500,
    rule: paletteColour(palette, PALETTE_KEYS.rule),
    // Cards sit on the light page (a memo is drawn on white paper), so the fallback is the light surface in any tone.
    card: paletteColour(palette, PALETTE_KEYS.card) ?? rawHex.structural[100],
  };
};

/** The theme's chart series colours, else the app's chart colours for its tone. */
export const themeChartRamp = (palette: Palette, tone: Tone): string[] => {
  const ramp = palette?.chartRamp;
  const own = Array.isArray(ramp) ? ramp.map(toHexColour).filter((colour): colour is string => colour !== null) : [];
  return own.length ? own : toChartTheme({ mode: tone, colors: TOKENS[tone] as ChartThemeSource["colors"] }).palette;
};

const toFrame = ({ x, y, w, h }: SlideFrame): SlideFrame => ({ x, y, w, h });

export const toThemeKitBox = (box: DistributionThemeKitBoxApi): ThemeKitBox => ({
  token: box.token,
  role: box.role,
  index: box.index ?? null,
  frame: toFrame(box),
  style: {
    sizePt: box.font_pt,
    fontFamily: box.font_family?.trim() || null,
    bold: box.bold,
    italic: box.italic,
    color: toHexColour(box.color),
    align: box.align,
    valign: box.valign,
  },
  template: box.template ?? null,
});

/** Numbered-group artwork by group count: "2" → 2; keys that aren't counts are dropped. */
const toVariants = (variants: DistributionThemeKitSlideApi["variants"]): Record<number, string> =>
  Object.fromEntries(
    Object.entries(variants ?? {}).flatMap(([n, variant]) => {
      const count = Number(n);
      return Number.isInteger(count) && count > 0 && variant?.background_url ? [[count, variant.background_url]] : [];
    }),
  );

export const toThemeKitSlide = (kind: string, slide: DistributionThemeKitSlideApi): ThemeKitSlide => ({
  kind,
  backgroundUrl: slide.background_url || null,
  variants: toVariants(slide.variants),
  mark: slide.mark ? { frame: toFrame(slide.mark), imageUrl: slide.mark.image_url || null } : null,
  boxes: (slide.boxes ?? []).map(toThemeKitBox),
});

/** The preview kit, or null before it is built, for another kit version, or without a slide size. */
export const toThemeKit = (kit: DistributionThemeKitApi | null | undefined): ThemeKit | null => {
  if (!kit || kit.version !== THEME_KIT_VERSION) return null;
  const widthPt = kit.slide?.width_pt ?? 0;
  const heightPt = kit.slide?.height_pt ?? 0;
  if (!(widthPt > 0 && heightPt > 0)) return null;
  return {
    widthPt,
    heightPt,
    stale: Boolean(kit.stale),
    slides: Object.fromEntries(Object.entries(kit.slides ?? {}).map(([kind, slide]) => [kind, toThemeKitSlide(kind, slide)])),
  };
};

export const toThemeFonts = (fonts: readonly DistributionThemeFontApi[] | null | undefined): ThemeFont[] =>
  (fonts ?? [])
    .filter((font) => font.family?.trim() && font.url)
    .map(({ family, weight, style, url }) => ({ family: family.trim(), weight: weight || "400", style: style || "normal", url }));

export const toThemeLook = (theme: DistributionThemeApi): ThemeLook => ({
  key: theme.id,
  label: theme.name || theme.id,
  description: theme.description?.trim() || null,
  tone: theme.tone,
  colors: themeLookColours(theme.palette, theme.tone),
  chartRamp: themeChartRamp(theme.palette, theme.tone),
  fontFamily: theme.font_family?.trim() || null,
  fonts: toThemeFonts(theme.fonts),
  kit: toThemeKit(theme.kit),
});

/** The catalog as looks, and its default (the first theme), for `useDistributionThemeLooks`. */
export const toThemeLooks = (items: readonly DistributionThemeApi[]): { looks: ThemeLook[]; defaultKey: string | undefined } => ({
  looks: items.map(toThemeLook),
  defaultKey: items[0]?.id,
});

/** An uploaded template's design as a catalog theme, so `toThemeLook` draws it like any theme. */
export const masterLookAsTheme = (look: DistributionMasterLookApi): DistributionThemeApi => ({
  id: look.master_id,
  name: look.file_name,
  description: null,
  tone: look.tone ?? "light",
  palette: look.palette,
  font_family: look.font_family ?? null,
  fonts: look.fonts ?? [],
  kit: look.kit ?? null,
});
